import { useRef } from "react";

import CompositeColorPicker from "@/appComponents/Blocks/Composites/CompositeColorPicker/CompositeColorPicker";
import ToolbarPopover from "../ToolbarPopover/ToolbarPopover";
import { ColorTrigger, Flex, Radio, Text } from "@theorgchartdev/storybook";
import { getHoverTipContent } from "@/Utils/Misc";
import { UpdateBoxProperty } from "@/Utils/StateLib";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { enumUpdateProperty, enumFillMode } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { editModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

export const CellFillModeComposite = ({ dataTestId } = {}) => {
  const wsMgrContext = useWSMgrContext();
  const openCtrlRef = useRef(null);
  const wsMgr = wsMgrContext?.wsMgr.state;
  if (!wsMgr) {
    return;
  }

  const value = wsMgr.SurveySelection(enumUpdateProperty.cellFillColor);
  if (value == null) {
    return;
  }

  const handleChange = event => {
    const fFillMode = parseInt(event.target.value);
    const result = { ...value, fFillMode };

    // Assign default color from QColorPicker.ExecuteCommand
    if (!result.fSelectedColor) {
      result.fSelectedColor = "#F2F2F2";
    }

    UpdateBoxProperty(enumUpdateProperty.cellFillColor, result);
  };

  const handleColorChange = key => selectedColor => {
    const result = { ...value, [key]: selectedColor };
    UpdateBoxProperty(enumUpdateProperty.cellFillColor, result);
  };

  const ColorPickerNode = (
    <Flex gap="12px">
      <CompositeColorPicker
        trigger={<ColorTrigger iconName={"droplet"} size={"small"} />}
        onChange={handleColorChange("fSelectedColor")}
        currentColor={value.fSelectedColor}
      />
      <CompositeColorPicker
        trigger={<ColorTrigger iconName={"droplet"} size={"small"} />}
        onChange={handleColorChange("fSelectedColor2")}
        currentColor={value.fSelectedColor2}
      />
    </Flex>
  );

  const standardFillSelected = [
    enumFillMode.noFill,
    enumFillMode.solidFill,
    enumFillMode.linearGradientDown
  ].includes(value.fFillMode);

  return (
    <ToolbarPopover
      openCtrlRef={openCtrlRef}
      icon={"paintbrush"}
      triggerTestId={dataTestId ? `${dataTestId}-btn` : editModeTestId("cell-fill-mode-btn")}
      otherPopoverProps={{
        floatingProps: {
          "data-testid": dataTestId
            ? `${dataTestId}-popover`
            : editModeTestId("cell-fill-mode-popover")
        }
      }}
      iconProps={{
        tooltipProps: { ...getHoverTipContent("Cell Fill Mode"), referenceOpenCtrlRef: openCtrlRef }
      }}
    >
      <Flex direction="column" gap="8px">
        <Text modifier={"medium"} size={"small"}>
          {appstr("Cell Fill")}
        </Text>
        <Radio
          id="cell-fill-mode-solidfill"
          checked={standardFillSelected}
          value={enumFillMode.solidFill}
          onChange={handleChange}
          label={appstr("solidfill")}
        />
        <Radio
          id="cell-fill-mode-separator"
          checked={enumFillMode.separator === value.fFillMode}
          value={enumFillMode.separator}
          onChange={handleChange}
          label={appstr("separator")}
        />
        <Radio
          id="cell-fill-mode-dashline"
          checked={enumFillMode.dashline === value.fFillMode}
          value={enumFillMode.dashline}
          onChange={handleChange}
          label={appstr("dashline")}
        />
        <Flex justifyContent="space-between">
          <Radio
            id="cell-fill-mode-triangle"
            checked={enumFillMode.triangle === value.fFillMode}
            value={enumFillMode.triangle}
            onChange={handleChange}
            label={appstr("triangle")}
          />
          {enumFillMode.triangle === value.fFillMode && ColorPickerNode}
        </Flex>
        <Flex justifyContent="space-between">
          <Radio
            id="cell-fill-mode-lefttriangle"
            checked={enumFillMode.lefttriangle === value.fFillMode}
            value={enumFillMode.lefttriangle}
            onChange={handleChange}
            label={appstr("lefttriangle")}
          />
          {enumFillMode.lefttriangle === value.fFillMode && ColorPickerNode}
        </Flex>
      </Flex>
    </ToolbarPopover>
  );
};
